import { defineCrestModule } from '@crest/platform-contracts/modules';
import { platformOptionalActionProviderHostCapability, platformOptionalFactProviderHostCapability, platformApprovedActionInvocationReceiptCapability, platformReceiptBoundActionProviderHostCapability, } from '@crest/platform-contracts/hosts';
import { workforceActivityRecordsCapability } from './contracts/workforce-activity.contract.js';
import { workforceDirectoryCapability, workforceEmployeeProfilesCapability, } from './contracts/workforce-profile.contract.js';
import { workforceEmployeeProfileHostCapability } from './contracts/workforce-employee-profile-host.contract.js';
import { workforceDocumentsProtectedFormEligibilityCapability, workforceDocumentsProtectedFormEligibilityProviderId, workforceDocumentsProtectedFormSubmissionCapability, workforceDocumentsProtectedFormSubmissionProviderId, workforceHrFormDefinitionsCapability, } from './contracts/workforce-documents-protected-form.contract.js';
import { workforceAssistantActivityActionProviderCapability } from './contracts/workforce-assistant-activity-actions.contract.js';
import { platformImportResolutionCapability } from '@crest/platform-contracts/import-resolution';
import { workforceActivityImportCapability } from './import/workforce-import.contract.js';
export const workforceModuleManifest = defineCrestModule({
    id: 'workforce',
    version: '1.0.0',
    kind: 'business',
    required: false,
    requires: ['platform'],
    requiredCapabilities: [
        'platform.tenant-context.v1',
        'platform.module-access.v1',
        'platform.membership-directory.v1',
        platformImportResolutionCapability,
        platformOptionalFactProviderHostCapability,
        platformOptionalActionProviderHostCapability,
        platformReceiptBoundActionProviderHostCapability,
        platformApprovedActionInvocationReceiptCapability,
    ],
    optionalCapabilities: [],
    optionalCapabilityFallbacks: [],
    provides: [
        workforceDirectoryCapability,
        workforceEmployeeProfilesCapability,
        workforceActivityRecordsCapability,
        workforceEmployeeProfileHostCapability,
        workforceHrFormDefinitionsCapability,
        workforceDocumentsProtectedFormEligibilityCapability,
        workforceDocumentsProtectedFormSubmissionCapability,
        workforceAssistantActivityActionProviderCapability,
        workforceActivityImportCapability,
    ],
    permissions: [
        'workforce.directory.read',
        'workforce.profiles.read-own',
        'workforce.profiles.read-all',
        'workforce.profiles.manage',
        'workforce.activity.read',
        'workforce.activity.manage',
        'workforce.activity.export',
        'workforce.import.read',
        'workforce.import.execute',
        'workforce.import.rollback',
        'workforce.hr-forms.read',
        'workforce.hr-forms.manage',
        'workforce.hr-forms.submit-own',
        'workforce.hr-forms.submit-all',
        'workforce.hr-forms.submissions.read-own',
        'workforce.hr-forms.submissions.read-all',
    ],
    settings: [
        'workforce.directory.configuration',
        'workforce.directory.display',
        'workforce.copy.directory',
        'workforce.copy.activity',
        'workforce.protected-forms.display',
        'workforce.copy.protected-forms',
    ],
    copyCatalogs: [
        'workforce.copy.directory',
        'workforce.copy.activity',
        'workforce.copy.protected-forms',
    ],
    events: {
        publishes: [
            'workforce.employee-profile-updated.v1',
            'workforce.employee-profile-archived.v1',
            'workforce.employee-profile-restored.v1',
            'workforce.activity-record-created.v1',
            'workforce.activity-record-updated.v1',
            'workforce.activity-record-archived.v1',
            'workforce.activity-record-restored.v1',
            'workforce.hr-form-definition-create.v1',
            'workforce.hr-form-definition-append-version.v1',
            'workforce.hr-form-definition-publish.v1',
            'workforce.hr-form-definition-archive.v1',
            'workforce.hr-form-definition-restore.v1',
            'workforce.hr-form-definition-rollback.v1',
            'workforce.protected-form-submission-recorded.v1',
        ],
        subscribes: [],
    },
    server: {
        routes: [
            { id: 'workforce.routes.directory', priority: 0 },
            { id: 'workforce.routes.profiles', priority: 10 },
            { id: 'workforce.routes.activity', priority: 20 },
            {
                id: 'workforce.routes.hr-form-definitions',
                priority: 30,
            },
            {
                id: 'workforce.routes.protected-form-submission-receipts',
                priority: 40,
            },
        ],
        jobs: [],
        subscribers: [],
        slots: [{
                id: workforceDocumentsProtectedFormEligibilityProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                priority: 0,
            }, {
                id: workforceDocumentsProtectedFormSubmissionProviderId,
                requiredCapability: platformOptionalActionProviderHostCapability,
                priority: 10,
            }, {
                id: workforceAssistantActivityActionProviderCapability,
                requiredCapability: platformReceiptBoundActionProviderHostCapability,
                requiredPermission: 'workforce.activity.manage',
                priority: 20,
            }],
    },
    web: {
        routes: [
            { id: 'workforce.web.directory', priority: 0 },
            {
                id: 'workforce.web.protected-forms',
                requiredPermission: 'workforce.hr-forms.manage',
                priority: 10,
            },
        ],
        navigation: [
            { id: 'workforce.navigation.directory', priority: 75 },
            {
                id: 'workforce.navigation.protected-forms',
                requiredPermission: 'workforce.hr-forms.manage',
                priority: 76,
            },
        ],
        slots: [],
    },
    mobile: { routes: [], slots: [] },
    portal: { routes: [], slots: [] },
    database: {
        schema: 'workforce',
        owns: [
            'workforce.employee_profiles',
            'workforce.employee_profile_versions',
            'workforce.employee_profile_commands',
            'workforce.activity_records',
            'workforce.activity_record_versions',
            'workforce.activity_record_commands',
            'workforce.list_employee_directory',
            'workforce.get_employee_profile',
            'workforce.list_employee_profile_versions',
            'workforce.execute_employee_profile_command',
            'workforce.list_activity_records',
            'workforce.get_activity_record',
            'workforce.list_activity_record_versions',
            'workforce.execute_activity_record_command',
            'workforce.hr_form_definitions',
            'workforce.hr_form_definition_versions',
            'workforce.hr_form_definition_commands',
            'workforce.protected_form_submission_receipts',
            'workforce.protected_form_submission_commands',
            'workforce.list_hr_form_definitions',
            'workforce.get_hr_form_definition',
            'workforce.list_hr_form_definition_versions',
            'workforce.execute_hr_form_definition_command',
            'workforce.current_protected_form_eligibility_v1',
            'workforce.record_protected_form_submission_v1',
            'workforce.list_protected_form_submission_receipts',
            'workforce.assistant_activity_create_origins',
            'workforce.assistant_activity_update_origins',
            'workforce.assistant_activity_archive_commands',
            'workforce.assistant_activity_supersession_commands',
            'workforce.ecmascript_trim_v1',
            'workforce.utf16_length_v1',
            'workforce.sha256_json_v1',
            'workforce.sha256_utf8_text_v1',
            'workforce.zod_uuid_valid_v1',
            'workforce.normalize_assistant_activity_create_input_v1',
            'workforce.normalize_assistant_activity_update_input_v1',
            'workforce.normalize_assistant_activity_archive_input_v1',
            'workforce.normalize_assistant_activity_supersession_input_v1',
            'workforce.lock_assistant_activity_approved_receipt_v1',
            'workforce.reject_assistant_activity_evidence_mutation_v1',
            'workforce.apply_assistant_activity_create_v1',
            'workforce.apply_assistant_activity_update_v1',
            'workforce.archive_assistant_created_activity_v1',
            'workforce.supersede_assistant_activity_revision_v1',
            'workforce.activity_import_runs',
            'workforce.activity_import_identity_map',
            'workforce.activity_import_receipts',
            'workforce.activity_import_rejects',
            'workforce.activity_import_rollbacks',
            'workforce.activity_import_canonical_json',
            'workforce.activity_import_sha256',
            'workforce.activity_import_payload_keys',
            'workforce.activity_import_deterministic_id',
            'workforce.activity_import_expected_target_id',
            'workforce.activity_import_receipt_json',
            'workforce.activity_import_authorized',
            'workforce.activity_import_assert_membership_resolution',
            'workforce.begin_activity_import_v1',
            'workforce.load_activity_import_run_v1',
            'workforce.record_activity_import_reject_v1',
            'workforce.apply_activity_import_row_v1',
            'workforce.rollback_activity_import_row_v1',
            'workforce.complete_activity_import_v1',
            'workforce.fail_activity_import_v1',
            'workforce.mark_activity_import_rolled_back_v1',
        ],
        readsThrough: [
            'platform.membership-directory.v1',
            platformImportResolutionCapability,
            platformApprovedActionInvocationReceiptCapability,
        ],
        migrations: [
            'workforce.directory_activity_foundation',
            'workforce.documents_protected_employee_forms',
            'workforce.assistant_activity_actions',
            'workforce.activity_import_reconciliation',
        ],
    },
    health: ['workforce.database'],
    lifecycle: {
        initialization: { timeoutMs: 5_000, failureBehavior: 'quarantine-module' },
        resources: {
            requestConcurrency: 64,
            jobConcurrency: 0,
            queueBacklogLimit: 0,
            databaseConnectionBudget: 8,
            workerIsolation: 'shared-api',
        },
        disable: {
            data: 'retain',
            clientContributions: 'remove',
            serverRoutes: 'deny-tenant-access',
            scheduledJobs: 'skip',
            events: {
                behavior: 'drop-safe',
                durableCursor: false,
                retentionDays: 0,
                backlogLimit: 0,
                warningAt: 0,
                reconciliationProcedure: null,
                dropSafeReason: 'The Workforce foundation publishes facts but owns no subscriber or worker.',
            },
            outboundSideEffects: 'suppress-no-retroactive-send',
            inFlight: 'finish-idempotently',
        },
        resume: {
            configuration: 'last-compatible-published',
            healthChecks: ['workforce.database'],
            reconciliationProcedure: null,
            visibility: 'after-health-and-reconciliation',
        },
        uninstall: {
            executableCode: 'remove',
            data: 'retain',
            tombstone: 'retain-immutable',
            destruction: 'separate-explicit-authority',
        },
    },
    retentionPolicy: 'Workforce profiles, immutable versions, coaching/activity history, and command evidence remain stored while disabled.',
});
//# sourceMappingURL=workforce.module.js.map